import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Paper, TextField, Button, Grid, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import { useTheme } from '@mui/material';
import Spinner from '../components/Spinner';

const Drugs = () => {
    const [drugs, setDrugs] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedDrug, setSelectedDrug] = useState(null);
    const [newDrug, setNewDrug] = useState({
        NombreComercial: '',
        NombreGenerico: '',
        Descripcion: '',
        Contraindicaciones: '',
        Presentacion: '',
        Laboratorio: ''
    });
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDrugId, setDeleteDrugId] = useState(null);

    const [openViewModal, setOpenViewModal] = useState(false); // New state for the modal
    const [isLoading, setIsLoading] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL;

    const handleRowClick = (params, isActionButton) => {
        if (isActionButton) { 
            if (params.field === 'actions') {
                const action = params.value;
                if (action === 'Modificar') {
                    handleSelectDrug(params.row);
                } else if (action === 'Eliminar') {
                    handleDeleteClickOpen(params.row.id);
                } 
            }
        }else{
            setSelectedDrug(params.row);
            setOpenViewModal(true);
        }
    };

    const fetchDrugs = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/drugs`);
            setDrugs(response.data.map((drug, index) => ({
                id: drug.id,
                NombreComercial: drug.NombreComercial ?? 'N/A',
                NombreGenerico: drug.NombreGenerico ?? 'N/A',
                Descripcion: drug.Descripcion ?? 'N/A',
                Contraindicaciones: drug.Contraindicaciones ?? 'N/A',
                Presentacion: drug.Presentacion ?? 'N/A',
                Laboratorio: drug.Laboratorio ?? 'N/A'
            })));
        } catch (error) {
            console.error('Error al obtener los datos de los fármacos:', error);
        } finally {
            setIsLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchDrugs();
    }, [fetchDrugs]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewDrug({ ...newDrug, [name]: value });
    };

    const generateUniqueId = () => {
        return '_' + Math.random().toString(36).substr(2, 9);
    };

    const handleAddDrug = async () => {
        try {
            const uniqueId = generateUniqueId();
            const drugToAdd = { ...newDrug, id: uniqueId }; // Adding unique ID to the drug
            const response = await axios.post(`${API_URL}/api/drugs`, drugToAdd);
            setDrugs([...drugs, { ...response.data, id: uniqueId }]); // Using the same unique ID
        } catch (error) {
            console.error('Error al agregar el fármaco:', error);
        }
        setNewDrug({
            NombreComercial: '',
            NombreGenerico: '',
            Descripcion: '',
            Contraindicaciones: '',
            Presentacion: '',
            Laboratorio: ''
        });
        setOpen(false);
    };

    const handleSelectDrug = (drug) => {
        setSelectedDrug(drug);
        setNewDrug(drug);
        setOpen(true);
    };

    const handleUpdateDrug = async () => {
        try {
            await axios.put(`${API_URL}/api/drugs/${newDrug.id}`, newDrug);
            setDrugs(drugs.map(drug => drug.id === newDrug.id ? newDrug : drug));
        } catch (error) {
            console.error('Error al modificar el fármaco:', error);
        }
        setSelectedDrug(null);
        setNewDrug({
            NombreComercial: '',
            NombreGenerico: '',
            Descripcion: '',
            Contraindicaciones: '',
            Presentacion: '',
            Laboratorio: ''
        });
        setOpen(false);
    };

    const handleDeleteDrug = async () => {
        try {
            await axios.delete(`${API_URL}/api/drugs/${deleteDrugId}`);
            setDrugs(drugs.filter(drug => drug.id !== deleteDrugId));
        } catch (error) {
            console.error('Error al eliminar el fármaco:', error);
        }
        setDeleteDrugId(null);
        setDeleteDialogOpen(false);
    };

    const handleClickOpen = () => {
        setSelectedDrug(null);
        setNewDrug({
            NombreComercial: '',
            NombreGenerico: '',
            Descripcion: '',
            Contraindicaciones: '',
            Presentacion: '',
            Laboratorio: ''
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteClickOpen = (id) => {
        setDeleteDrugId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteClickClose = () => {
        setDeleteDialogOpen(false);
    };

    const columns = [
        { field: 'NombreComercial', headerName: 'Nombre Comercial', width: 150 },
        { field: 'NombreGenerico', headerName: 'Nombre Genérico', width: 150 },
        { field: 'Descripcion', headerName: 'Descripción', width: 250 },
        { field: 'Contraindicaciones', headerName: 'Contraindicaciones', width: 100 },
        { field: 'Presentacion', headerName: 'Presentación', width: 100 },
        { field: 'Laboratorio', headerName: 'Laboratorio', width: 100 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 300,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small"
                        startIcon={<Edit />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectDrug(params.row);
                        }}
                        sx={{ marginRight: 1 }}
                    >
                        Modificar
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        size="small"
                        startIcon={<Delete />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClickOpen(params.row.id);
                        }}
                    >
                        Eliminar
                    </Button>
                </Box>
            )
        }
    ];

    const filteredDrugs = drugs.filter(drug => 
        drug.NombreComercial.toLowerCase().includes(search.toLowerCase())
    );

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 5,
        page: 0,
    });

    const theme = useTheme();

    return (
        <Box 
            className="center-align" 
            sx={{
                display: 'flex',
                padding: 2, // Espaciado general
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column' 
            }}
        >
            <Paper 
                elevation={3} 
                sx={{ 
                    padding: { xs: 2, sm: 4 }, // Padding responsive
                    width: '100%',
                    maxWidth: '98vw',
                    overflowX: 'auto',
                    borderRadius: 2,
                    margin: 'auto',
                    boxSizing: 'border-box',
                    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`, // Añade el estilo de fondo
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
            >
                <Spinner isLoading={isLoading} message="Cargando fármacos..." />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    Gestión de Fármacos</Typography>
                <Grid container spacing={3} sx={{ marginBottom: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField 
                            label="Buscar Fármaco" 
                            variant="outlined" 
                            fullWidth 
                            margin="normal" 
                            value={search} 
                            onChange={handleSearchChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<Add />}
                            onClick={handleClickOpen}
                            sx={{ height: 'fit-content' }}
                        >
                            Agregar Fármaco
                        </Button>
                    </Grid>
                </Grid>
                <div style={{height: '60vh', width: '100%', minHeight: '300px'}}>
                    {!isLoading && (
                        <DataGrid
                            rows={filteredDrugs}
                            columns={columns}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[5]}
                            density="compact"
                            autoPageSize
                            disableSelectionOnClick
                            components={{
                                Toolbar: GridToolbar
                            }}
                            onRowClick={(params) => handleRowClick(params, false)}
                        />
                    )}
                </div>
                <Dialog 
                    open={open} 
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                        handleClose();
                        }
                    }}
                    aria-labelledby="form-dialog-title"
                    disableEscapeKeyDown
                    >
                    <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.primary.main }} id="form-dialog-title">{selectedDrug ? 'Modificar Fármaco' : 'Agregar Fármaco'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {selectedDrug ? 'Modificar los detalles del fármaco.' : 'Ingrese los detalles del nuevo fármaco.'}
                        </DialogContentText>
                        <TextField 
                            autoFocus
                            margin="dense"
                            label="Nombre Comercial"
                            type="text"
                            fullWidth
                            name="NombreComercial"
                            value={newDrug.NombreComercial}
                            onChange={handleInputChange}
                        />
                        <TextField 
                            margin="dense"
                            label="Nombre Genérico"
                            type="text"
                            fullWidth
                            name="NombreGenerico"
                            value={newDrug.NombreGenerico}
                            onChange={handleInputChange}
                        />
                        <TextField 
                            margin="dense"
                            label="Descripción"
                            type="text"
                            fullWidth
                            name="Descripcion"
                            value={newDrug.Descripcion}
                            onChange={handleInputChange}
                            multiline
                            rows={10}
                        />
                        <TextField 
                            margin="dense"
                            label="Contraindicaciones"
                            type="text"
                            fullWidth
                            name="Contraindicaciones"
                            value={newDrug.Contraindicaciones}
                            onChange={handleInputChange}
                            multiline
                            rows={2}
                        />
                        <TextField 
                            margin="dense"
                            label="Presentación"
                            type="text"
                            fullWidth
                            name="Presentacion"
                            value={newDrug.Presentacion}
                            onChange={handleInputChange}
                        />
                        <TextField 
                            margin="dense"
                            label="Laboratorio"
                            type="text"
                            fullWidth
                            name="Laboratorio"
                            value={newDrug.Laboratorio}
                            onChange={handleInputChange}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="primary">
                            Cancelar
                        </Button>
                        <Button onClick={selectedDrug ? handleUpdateDrug : handleAddDrug} color="primary">
                            {selectedDrug ? 'Modificar' : 'Agregar'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog 
                    open={deleteDialogOpen} 
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                        handleDeleteClickClose();
                        }
                    }}
                    aria-labelledby="delete-dialog-title"
                    disableEscapeKeyDown
                    >
                    <DialogTitle id="delete-dialog-title">Confirmar Eliminación</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            ¿Estás seguro de que deseas eliminar este fármaco?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteClickClose} color="primary">
                            Cancelar
                        </Button>
                        <Button onClick={handleDeleteDrug} color="secondary">
                            Eliminar
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog 
                    open={openViewModal} 
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                        setOpenViewModal(false);
                        }
                    }}
                    maxWidth="lg" 
                    fullWidth
                    disableEscapeKeyDown
                    >
                    <DialogTitle sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Detalles del Fármaco</DialogTitle>
                    <DialogContent dividers>
                        {selectedDrug && (
                            <Box sx={{ p: 2 }}>
                                <DialogContentText>
                                    <strong>Nombre Comercial:</strong> {selectedDrug?.NombreComercial}<br />
                                    <strong>Nombre Genérico:</strong> {selectedDrug?.NombreGenerico}<br />
                                    <strong>Descripción:</strong> {selectedDrug?.Descripcion}<br />
                                    <strong>Contraindicaciones:</strong> {selectedDrug?.Contraindicaciones}<br />
                                    <strong>Presentación:</strong> {selectedDrug?.Presentacion}<br />
                                    <strong>Laboratorio:</strong> {selectedDrug?.Laboratorio}<br />
                                </DialogContentText>
                                <DialogActions>
                                    <Button onClick={() => setOpenViewModal(false)} color="primary">
                                        Cerrar
                                    </Button>
                                </DialogActions>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default Drugs;